import { Onboarding } from "./pages/onboarding/onboarding";
import './assets/default.css';
import 'react-resizable/css/styles.css';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import 'react-quill/dist/quill.snow.css';
import "./app.css";
import "./styles/all-styles.css";
import { SupportBanner } from './components/supportBanner/SupportBanner';
import { SupportRequestModal } from './components/supportBanner/SupportRequestModal';
import { RemoteCursor } from './components/remoteCursor/RemoteCursor';
import { WhatsNewModal } from './components/whatsNew/WhatsNewModal';
import { setActiveSession } from './middleware/redux/reducers/support.reducer';
 
import {BrowserRouter, Route, Routes, Navigate, useLocation, useNavigate, matchPath} from 'react-router-dom';
import React, { Suspense, useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import { notification } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { setNotification, notificationConsumed } from './middleware/redux/reducers/notification.reducer';
import { ApolloProvider, useLazyQuery, useQuery } from '@apollo/client';
import { privateClient, publicClient } from './config/apollo';
import { useErrorBoundary } from './util/errorBoundary/errorboundary';
import { ErrorFallback } from './util/errorFallback/errorFallback';
import { OfflineFallback } from './util/offlineFallback/offlineFallback';
import { isLoginCheckQuery } from './util/query/user.query';
import { PrivateRoutes } from './util/routes/private.routes';
import Spinner from './components/spinner';
import { IncidentReportingDetailPage } from './pages/incidentReporting/incidentReportingDetail.page';
import { MobileServices } from './pages/mobileServices/mobileServices.page';
import { TaskLibrary } from './pages/taskLibrary/taskLibrary.page';
import RelayOnVPI from './reports/mobileReports/relayOn/relayOnVPI';
import RelayOnAlarm from './reports/mobileReports/relayOn/relayOnAlarm';
import JourneyTimeline from './components/journeyTimelineMap/journeyTimlineMap.component';
import {Dashboard} from './pages/dashboard/dashboard';
import { TimeSheet } from './pages/timeSheet/timeSheet.page';
import { NotificationPage } from './pages/notifications/notifications.page';
import PatrolReportViewer from './components/checkCallReportActivity/checkCallReportActivity';
import { GenericMobileReport } from './reports/genericMobileReport/genericMobileReport';
import EmployeeMap from './pages/employee/employeeMap';
import MapDashboard from './pages/employee/mapDashboard';
import MobileResponseServices from './pages/mobileResponseServices/mobileResponseServices';
import { SystemReportMobileServiceScheduleView } from './pages/systemReports/reportViews/mobileServiceScheduleReport';
import { SystemReportMobileServicePayView } from './pages/systemReports/reportViews/mobileServicePayReport';
import { SystemReportMobileServiceBillView } from './pages/systemReports/reportViews/mobileServiceBillReport';
import { KeyRegisterPage } from './pages/keyRegister/keyRegister.page';
import { CompliancePage } from './pages/compliance/compliance.page';
import { AppraisalsPage } from './pages/appraisals/appraisals.page';
import { SurveysPage } from './pages/surveys/surveys.page';
import { SurveyAnswerPage } from './pages/surveyPublic/surveyAnswer.page';
import { EmbedAppraisalPage, EmbedDocumentPage, EmbedSurveyPage } from './pages/embed/embedForm.page';
import KeyDetailPage from './pages/keyRegister/keyDetail.page';
import { KeyAuditConductModal } from './pages/keyRegister/keyAuditConduct.modal';
import { SystemReportMobileServiceBillForCustomerView } from './pages/systemReports/reportViews/mobileServicesBillReportForCustomer';
import { PsgAlarmReport } from './reports/mobileReports/psg/psgAlarm';
import { RecordAccessGate } from './components/recordAccessGate/recordAccessGate';

// Lazy load all components
const Login = React.lazy(() => import("./pages/login").then(module => ({ default: module.Login })));
const DefaultLayout = React.lazy(() => import("./layout/defaultLayout").then(module => ({ default: module.DefaultLayout })));
const Employee = React.lazy(() => import("./pages/employee").then(module => ({ default: module.Employee })));
const Branch = React.lazy(() => import("./pages/branch").then(module => ({ default: module.Branch })));
const ModuleFormBuilder = React.lazy(() => import("./components/moduleFormBuilder/ModuleFormBuilder").then(module => ({ default: module.ModuleFormBuilder })));
const FormView = React.lazy(() => import('./pages/formView/formView').then(module => ({ default: module.FormView })));
const BranchDetailPage = React.lazy(() => import('./pages/branchDetailPage/branchDetailPage').then(module => ({ default: module.BranchDetailPage })));
const AllProperties = React.lazy(() => import('./pages/allProperties/allProperties.page').then(module => ({ default: module.AllProperties })));
const BranchAllPropHistory = React.lazy(() => import('./pages/branchAllPropHistory/branchAllPropHistory.page').then(module => ({ default: module.BranchAllPropHistory })));
const SiteGroup = React.lazy(() => import('./pages/sitegroup/sitegroup.page').then(module => ({ default: module.SiteGroup })));
const EmployeeDetailPage = React.lazy(() => import('./pages/employeeDetailPage/employeeDetail.page').then(module => ({ default: module.EmployeeDetailPage })));
const EmployeeAllPropHistory = React.lazy(() => import('./pages/employeeAllPropHistory/employeeAllPropHistory').then(module => ({ default: module.EmployeeAllPropHistory })));
const PendingEmployees = React.lazy(() => import('./pages/pendingEmployees').then(module => ({ default: module.PendingEmployees })));
const Password = React.lazy(() => import('./pages/login/password').then(module => ({ default: module.Password })));
const Sso = React.lazy(() => import('./pages/sso/sso').then(module => ({ default: module.Sso })));
const UnAuthroizedAccess = React.lazy(() => import('./pages/unAuthroizedAccess/unAuthroizedAccess.page').then(module => ({ default: module.UnAuthroizedAccess })));
const PageNotFound = React.lazy(() => import('./pages/unAuthroizedAccess/pageNotFound.page').then(module => ({ default: module.PageNotFound })));
const Join = React.lazy(() => import('./pages/join/join').then(module => ({ default: module.Join })));
const UserPerference = React.lazy(() => import('./pages/setting/userPerference/userPerference').then(module => ({ default: module.UserPerference })));
const SiteGroupDetailPage = React.lazy(() => import('./pages/sitegroup/sitegroupDetailPage/sitegroupDetail.page').then(module => ({ default: module.SiteGroupDetailPage })));
const CustomerPage = React.lazy(() => import('./pages/customer/customer.page').then(module => ({ default: module.CustomerPage })));
const CustomerDetailPage = React.lazy(() => import('./pages/customer/customerDetailPage/customerDetail.page').then(module => ({ default: module.CustomerDetailPage })));
const AgencyPage = React.lazy(() => import('./pages/agency/agency.page').then(module => ({ default: module.AgencyPage })));
const AgencyDetailPage = React.lazy(() => import('./pages/agency/agencyDetailPage/agencyDetail.page').then(module => ({ default: module.AgencyDetailPage })));
const SitePage = React.lazy(() => import('./pages/site/site.page').then(module => ({ default: module.SitePage })));
const SiteDetailPage = React.lazy(() => import('./pages/site/siteDetailPage/siteDetail.page').then(module => ({ default: module.SiteDetailPage })));
const SchedulePage = React.lazy(() => import('./pages/schedule/schedule.page').then(module => ({ default: module.SchedulePage })));
const BulkRotaLoadPage = React.lazy(() => import('./pages/schedule/rota/bulkRotaLoad/bulkRotaLoad.page').then(module => ({ default: module.BulkRotaLoadPage })));
const RotaLoadBatchDetailPage = React.lazy(() => import('./pages/schedule/rota/rotaLoadBatch/rotaLoadBatchDetail.page').then(module => ({ default: module.RotaLoadBatchDetailPage })));
const RotaLoadHistoryPage = React.lazy(() => import('./pages/schedule/rota/rotaLoadHistory/rotaLoadHistory.page').then(module => ({ default: module.RotaLoadHistoryPage })));
const TaskPage = React.lazy(() => import('./pages/task/task.page').then(module => ({ default: module.TaskPage })));
const ExternalSiteLog = React.lazy(() => import('./pages/externalSiteLogPage/externalSiteLog.page').then(module => ({ default: module.ExternalSiteLog })));
const FilePreview = React.lazy(() => import('./pages/filePreview/filePreview').then(module => ({ default: module.FilePreview })));
const ShareDocument = React.lazy(() => import('./pages/shareDocument/shareDocument').then(module => ({ default: module.ShareDocument })));
const ExternalFilePreview = React.lazy(() => import('./pages/externalFilePreview/externalFilePreview').then(module => ({ default: module.ExternalFilePreview })));
const TimelinePage = React.lazy(() => import('./pages/timeline/timeline.page').then(module => ({ default: module.TimelinePage })));
const PublicAccessDutyReport = React.lazy(() => import('./pages/schedule/publicAccessDutyReport/publicAccessDutyReport').then(module => ({ default: module.PublicAccessDutyReport })));
const PublicAccessDutyReportLanding = React.lazy(() => import('./pages/schedule/publicAccessDutyReport/PublicAccessDutyReportLanding').then(module => ({ default: module.PublicAccessDutyReportLanding })));
const WardSecurityMobileReport = React.lazy(() => import('./reports/mobileReports/wardSecurity/wardSecurityMobileReport').then(module => ({ default: module.WardSecurityMobileReport })));
const SystemReport = React.lazy(() => import('./pages/systemReports/systemReports.page'));
const SystemReportPayBillView = React.lazy(() => import('./pages/systemReports/reportViews/systemReportPayBillView').then(module => ({ default: module.SystemReportPayBillView })));
const SystemReportEmployeePayDetailView = React.lazy(() => import('./pages/systemReports/reportViews/systemReportEmployeePayDetail').then(module => ({ default: module.SystemReportEmployeePayDetailView })));

const SystemReportEmployeePayDetailByEmployee = React.lazy(() => import('./pages/systemReports/reportViews/systemReportEmployeePayDetailByEmployee').then(module => ({ default: module.SystemReportEmployeePayDetailByEmployee })));

const SystemReportEmployeePaySummaryView = React.lazy(() => import('./pages/systemReports/reportViews/systemReportEmployeePaySummary').then(module => ({ default: module.SystemReportEmployeePaySummaryView })));
const SystemReportBillDetailedView = React.lazy(() => import('./pages/systemReports/reportViews/billDetailReport').then(module => ({ default: module.SystemReportBillDetailedView })));
const SystemReportKeyholdingBillingView = React.lazy(() => import('./pages/systemReports/reportViews/keyholdingBillingReport').then(module => ({ default: module.SystemReportKeyholdingBillingView })));
const SystemReportKeyholdingPayView = React.lazy(() => import('./pages/systemReports/reportViews/keyholdingPayReport').then(module => ({ default: module.SystemReportKeyholdingPayView })));
const SystemReportSiteScheduleView = React.lazy(() => import('./pages/systemReports/reportViews/siteScheduleReport').then(module => ({ default: module.SystemReportSiteScheduleView })));
const SystemReportSiteScheduleWeeklyView = React.lazy(() => import('./pages/systemReports/reportViews/siteScheduleWeeklyReport').then(module => ({ default: module.SystemReportSiteScheduleWeeklyView })));
const HoursPayBillAnalysisReportView = React.lazy(() => import('./pages/systemReports/reportViews/hoursPayBillAnalysisReport').then(module => ({ default: module.HoursPayBillAnalysisReportView })));
const SiaLicenseComplianceReportView = React.lazy(() => import('./pages/systemReports/reportViews/siaLicenseComplianceReport').then(module => ({ default: module.SiaLicenseComplianceReportView })));
const DataQualityReportView = React.lazy(() => import('./pages/systemReports/reportViews/dataQualityReport').then(module => ({ default: module.DataQualityReportView })));
const AlarmMonitoringReportView = React.lazy(() => import('./pages/systemReports/reportViews/alarmMonitoringReport').then(module => ({ default: module.AlarmMonitoringReportView })));
const BusinessAnalysisPage = React.lazy(() => import('./pages/businessAnalysis/businessAnalysis.page').then(module => ({ default: module.BusinessAnalysisPage })));
const HelpPage = React.lazy(() => import('./pages/help/help.page').then(module => ({ default: module.HelpPage })));
const InvoiceDutyItemsPage = React.lazy(() => import('./pages/businessAnalysis/invoiceDutyItems.page').then(module => ({ default: module.InvoiceDutyItemsPage })));
const AttandanceGapPage = React.lazy(() => import('./pages/attandancegapPage/attandanceGap.page').then(module => ({ default: module.AttandanceGapPage })));
const IncidentReportingPage = React.lazy(() => import('./pages/incidentReporting/incidentReporting.page').then(module => ({ default: module.IncidentReportingPage })));
const ComplaintRegisterPage = React.lazy(() => import('./pages/complainRegisterPage/complainRegister.page').then(module => ({ default: module.ComplaintRegisterPage })));
const ComplainRegisterDetailPage = React.lazy(() => import('./pages/complainRegisterPage/complainRegisterDetail.page').then(module => ({ default: module.ComplainRegisterDetailPage })));
const CustomerRequestsPage = React.lazy(() => import('./pages/customerRequests/customerRequests.page').then(module => ({ default: module.CustomerRequestsPage })));
const InvoiceList = React.lazy(() => import('./pages/setting/billing/invoices/invoiceList').then(module => ({ default: module.InvoiceList })));
const InvoiceView = React.lazy(() => import('./pages/setting/billing/invoices/invoiceView').then(module => ({ default: module.InvoiceView })));
const PayrollPage = React.lazy(() => import('./pages/payroll/payroll.page').then(module => ({ default: module.PayrollPage })));

// ComplainRegisterDetailPage

// Loading component for Suspense
const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spinner />
  </div>
);

// Process ?supportToken= from the URL immediately — before React renders —
// so that PrivateRoutes' sessionStorage check sees it on the very first render.
(function bootstrapSupportToken() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('supportToken');
  if (token) {
    sessionStorage.setItem('supportSessionToken', token);
    window.history.replaceState({}, '', window.location.pathname);
  }
})();

// The public and private route trees below are two independent sibling <Routes>
// under the same <BrowserRouter> — each matches the current URL on its own,
// unaware of the other. A bare `path='*'` catch-all in either tree would therefore
// ALSO fire (and render alongside) whenever the *other* tree has the real match —
// e.g. a naive catch-all here would double-render on top of "/pwd" or "/user/dashboard".
// This only renders the 404 page when neither tree's own routes match at all.
const PRIVATE_TREE_PATTERNS = ['/error', '/formview', '/branch/editform', '/employee/editform', '/user/*'];
const CatchAllRoute = () => {
  const location = useLocation();
  const handledByPrivateTree = PRIVATE_TREE_PATTERNS.some((pattern) => matchPath(pattern, location.pathname));
  if (handledByPrivateTree) return null;
  return <PageNotFound />;
};

// A record's detail page: a link to one outside the user's data access (or one
// that no longer exists) says so instead of opening a broken page.
const gate = (kind, noun, page) => <RecordAccessGate kind={kind} noun={noun}>{page}</RecordAccessGate>;

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setNotification({}));
  }, [dispatch]);

  useEffect(() => {
    const checkScreen = () => {
      // const isSmallScreen = window.innerWidth <= 1550;
      const isSmallScreen = window.innerWidth <= 1690;
  
      if (isSmallScreen) {
        document.body.classList.add("mac-small");
      } else {
        document.body.classList.remove("mac-small");
      }
    };
  
    checkScreen(); // Check initially
    window.addEventListener("resize", checkScreen);
  
    return () => window.removeEventListener("resize", checkScreen); // Cleanup
  }, []);

// In your App.tsx or main component
useEffect(() => {
  if ('serviceWorker' in navigator) {
    const messageHandler = (event) => {
      console.log("📨 Message from SW:", event.data);
      
      if (event.data && event.data.type === 'NAVIGATE') {
        console.log("🧭 Navigating to:", event.data.url);
        
        // Use React Router navigate if you have it
        // navigate(event.data.url);
        
        // Or use window location
        window.location.href = event.data.url;
      }
    };

    navigator.serviceWorker.addEventListener('message', messageHandler);

    return () => {
      navigator.serviceWorker.removeEventListener('message', messageHandler);
    };
  }
}, []);
  
  // Defaulted: browsers with state persisted (redux-persist) from before this
  // slice became a queue still have the old `{notificationToast: {}}` shape in
  // localStorage, which rehydrates with no `notificationQueue` key at all.
  const {notificationQueue = []} = useSelector(state => state.notificationReducer);
  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    // Drains one queued toast per render rather than reading a single
    // overwritten slot — two dispatches fired back-to-back in the same tick
    // (e.g. dismiss a "processing" toast, then show its result) land as two
    // separate queue entries instead of the first being lost to React batching
    // the pair into one render.
    if(notificationQueue.length > 0){
      const { key, placement, message, error, loading, close, duration } = notificationQueue[0];
      if(close){
        api.destroy(key);
      } else if(loading){
        // Non-blocking "processing" toast — stays open (duration: 0) until the
        // caller explicitly closes it (dispatch({close: true, key})), typically
        // right before showing the success/error toast for the same action.
        api.open({
          key,
          message,
          icon: <LoadingOutlined spin />,
          placement: placement || "top",
          duration: 0,
          className: 'notification-without-close',
        });
      } else if(error){
        api.error({
          key,
          message,
          placement: placement || "top",
          duration,
          className: 'notification-without-close',
        });
      } else {
        api.success({
          key,
          message,
          placement: placement || "top",
          duration,
          className: 'notification-without-close',
        });
      }
      dispatch(notificationConsumed());
    }
  }, [notificationQueue, api, dispatch]);

  const hasError = useErrorBoundary();

  useEffect(() => {
    // Check if the browser supports the Notification API
    if (!('Notification' in window)) {
      console.error('This browser does not support notifications.');
      return;
    }

    // Request permission for notifications
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        console.log('Notification permission granted.');
      } else {
        console.error('Notification permission denied.');
      }
    });
  }, []);


  const {authenticatedUserDetail} = useSelector(state=>state.userAuthReducer);
  const { activeSession } = useSelector(state => state.support);
  const isSupportSession = !!sessionStorage.getItem('supportSessionToken');
  const [supportModalOpen, setSupportModalOpen] = useState(false);

  // If a support token was bootstrapped from the URL, decode it and hydrate Redux.
  // sessionStorage was already set synchronously before first render.
  useEffect(() => {
    const supportToken = sessionStorage.getItem('supportSessionToken');
    if (!supportToken) return;
    try {
      const raw = supportToken.split('.')[1];
      const base64 = raw.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      if (payload.isAdminSession) {
        dispatch(setActiveSession({
          _id: payload.sessionId,
          tenantId: payload.tenantId,
          tenantName: payload.tenantName,
          accessLevel: payload.accessLevel,
          status: 'active',
        }));
      }
    } catch (_) {}
  }, [dispatch]);



  return (
    hasError ?
    <ErrorFallback /> :
    <>
      {contextHolder}

      <OfflineFallback />

      <BrowserRouter>
        {/* public routes */}
        <ApolloProvider client={publicClient}>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {authenticatedUserDetail?._id ? 
                <Route path='/' element={<Navigate to={"/user/dashboard"} />} /> :
                <Route path='/' element={<Login key="login"/>} />
              }
              <Route path="/pwd" element={<Password key="pwd"/>} />
              {/* one-time link from WFC Verify (shared login) */}
              <Route path="/sso" element={<Sso />} />
              <Route path="/site/:siteId/:siteName/log" element={<ExternalSiteLog />} />

              <Route path="/map" element={<JourneyTimeline />} />

              {/* schedule report */}
              <Route path='/schedulereporting/:templateId/:scheduleId/:siteId' element={<PublicAccessDutyReportLanding />} />
              <Route path='/schedulereport' element={<PublicAccessDutyReport />} />
              <Route path="/join/:employeeId" element={<Join />} />
              <Route path="/preview" element={<ExternalFilePreview />} />
              <Route path="/pdf" element={<PatrolReportViewer />} />

              {/* specific customer report design */}
              {/* Ward template path */}
              <Route path='wardreport/:jobId' exact element={<WardSecurityMobileReport />} />

              {/* Relyon template path  */}
              <Route path='relyOnVPI/:jobId' exact element={<RelayOnVPI />} />
              <Route path='relyOnAlarm/:jobId' exact element={<RelayOnAlarm />} />
              <Route path='psgAlarm/:jobId/:templateId' exact element={<PsgAlarmReport />} />

              <Route path='genericPatrol/:jobId/:templateId' exact element={<GenericMobileReport />} />

              <Route path='auditReport/:auditId/:keyId' element={<KeyAuditConductModal/>} />

              {/* survey link from an email - no login, the token is the key */}
              <Route path='survey/:token' element={<SurveyAnswerPage />} />
              {/* the app shows a survey / appraisal on the company's own form here, in a web view */}
              <Route path='embed/survey/:responseId' element={<EmbedSurveyPage />} />
              <Route path='embed/appraisal/:id' element={<EmbedAppraisalPage />} />
              <Route path='embed/document/:shareId' element={<EmbedDocumentPage />} />


              <Route path='*' element={<CatchAllRoute />} />

            </Routes>
          </Suspense>
        </ApolloProvider>
          
        {/* private Routes */}
        
          <ApolloProvider client={(authenticatedUserDetail?._id || isSupportSession) ? privateClient : publicClient}>
            {/* Support overlays inside ApolloProvider so useMutation/useQuery work */}
            
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Error Fall back route */}
                <Route path="/error" exact element={<UnAuthroizedAccess />} />
                
                <Route path='/formview' element={<FormView />} />
                <Route path='/branch/editform' exact element={<ModuleFormBuilder objectType="Branch" />} />
                <Route path='/employee/editform' exact element={<ModuleFormBuilder objectType="Employee" />} />


                {/* first-run setup: full screen, outside the portal's navbar and menus */}
                <Route path='/user/setup' element={<PrivateRoutes><Onboarding /></PrivateRoutes>} />

                {/* private routes */}
                <Route path='/user/' element={
                  <PrivateRoutes>
                    <DefaultLayout session={activeSession} />
                  </PrivateRoutes>
                }>

                  <Route path='dashboard' element={<Dashboard />} />
                  

                  {/* System reports */}
                  <Route path='sysreport/:category?' exact element={<SystemReport />} />
                  <Route path='sysreport/paybill' exact element={<SystemReportPayBillView />} />
                  <Route path='sysreport/employeePayDetail' exact element={<SystemReportEmployeePayDetailView />} />
                  
                  <Route path='sysreport/employeePayDetailByEmployee' exact element={<SystemReportEmployeePayDetailByEmployee />} />

                  <Route path='sysreport/employeePaySummary' exact element={<SystemReportEmployeePaySummaryView />} />
                  <Route path='sysreport/billDetailedReport' exact element={<SystemReportBillDetailedView />} />
                  <Route path='sysreport/siteSchedule' exact element={<SystemReportSiteScheduleView />} />
                  <Route path='sysreport/siteScheduleWeekly' exact element={<SystemReportSiteScheduleWeeklyView />} />
                  <Route path='sysreport/mobileServiceSchedule' exact element={<SystemReportMobileServiceScheduleView />} />

                  <Route path='sysreport/mobileServicePay' exact element={<SystemReportMobileServicePayView />} />
                  <Route path='sysreport/mobileServiceBill' exact element={<SystemReportMobileServiceBillView />} />
                  <Route path='sysreport/mobileServiceBillForCustomer' exact element={<SystemReportMobileServiceBillForCustomerView />} />
                  
                  <Route path='sysreport/keyholding' exact element={<SystemReportKeyholdingBillingView />} />
                  <Route path='sysreport/keyholdingpay' exact element={<SystemReportKeyholdingPayView />} />
                  <Route path='sysreport/hoursPayBillAnalysis' exact element={<HoursPayBillAnalysisReportView />} />
                  <Route path='sysreport/siaLicenseCompliance' exact element={<SiaLicenseComplianceReportView />} />
                  <Route path='sysreport/dataQuality' exact element={<DataQualityReportView />} />
                  <Route path='sysreport/alarmMonitoring' exact element={<AlarmMonitoringReportView />} />
                  <Route path='businessAnalysis' exact element={<BusinessAnalysisPage />} />
                  <Route path='help' exact element={<HelpPage />} />
                  <Route path='help/:slug' exact element={<HelpPage />} />

                  {/* Invoice routes */}
                  <Route path='invoices' exact element={<InvoiceList />} />
                  <Route path='invoiceview/:invoiceId' exact element={<InvoiceView />} />
                  <Route path='invoice-duty-items/:invoiceId' exact element={<InvoiceDutyItemsPage />} />

                  {/* Payroll export */}
                  <Route path='payroll' exact element={<PayrollPage />} />

                  <Route path='perference' exact element={<UserPerference />} />
                  <Route path='schedule/:siteId?' exact element={<SchedulePage />} />
                  <Route path='schedule/rota/bulk-load' exact element={<BulkRotaLoadPage />} />
                  <Route path='schedule/rota/load/:batchId' exact element={<RotaLoadBatchDetailPage />} />
                  <Route path='schedule/rota/history' exact element={<RotaLoadHistoryPage />} />
                  <Route path='preview' exact element={<FilePreview />} />
                  <Route path='share/:fileId' exact element={<ShareDocument />} />

                  <Route path='branch' exact element={<Branch />} />
                  <Route path='branch-detail/:id' element={gate('branch', 'branch', <BranchDetailPage />)} />
                  <Route path='branch-detail/:id/:edit?' element={gate('branch', 'branch', <BranchDetailPage />)} />
                  <Route path='branch-detail/:id/tab/:tab?' element={gate('branch', 'branch', <BranchDetailPage />)} />
                  <Route path='branch-detail/:id/tab/:tab?/:folderId?' element={gate('branch', 'branch', <BranchDetailPage />)} />

                  <Route path='prophistory' element={<BranchAllPropHistory />} />

                  <Route path='employee' element={<Employee />} />
                  <Route path='pending-employees' element={<PendingEmployees />} />
                  <Route path='employee-detail/:id/:edit?' element={gate('employee', 'employee', <EmployeeDetailPage />)} />
                  <Route path='employee-detail/:id/tab/:tab?/:documentId?' element={gate('employee', 'employee', <EmployeeDetailPage />)} />
                  <Route path='employee-detail/:id/tab/:tab?/folder/:documentId?' element={gate('employee', 'employee', <EmployeeDetailPage />)} />
                  <Route path='employee-prop-history/:id' element={<EmployeeAllPropHistory />} />
                  <Route path='employeemap' element={<MapDashboard/>}/>

                  <Route path='allproperties' element={<AllProperties />} />
                  
                  {/* site group */}
                  <Route path='sitegroup' element={<SiteGroup />} />
                  <Route path='sitegroup-detail/:id/:edit?' element={gate('sitegroup', 'site group', <SiteGroupDetailPage />)} />
                  <Route path='sitegroup-detail/:id/tab/:tab' element={gate('sitegroup', 'site group', <SiteGroupDetailPage />)} />

                  {/* site */}
                  <Route path='site' element={<SitePage />} />
                  <Route path='site-detail/:id/:edit?' element={gate('site', 'site', <SiteDetailPage />)} />
                  <Route path='site-detail/:id/tab/:tab/:operationTab?' element={gate('site', 'site', <SiteDetailPage />)} />
                  <Route path='site-detail/:id/tab/:tab?/folder/:documentId?' element={gate('site', 'site', <SiteDetailPage />)} />

                  {/* Customer */}
                  <Route path='customer' element={<CustomerPage />} />
                  <Route path='customer-detail/:id' element={gate('customer', 'customer', <CustomerDetailPage />)} />
                  <Route path='customer-detail/:id/:edit?' element={gate('customer', 'customer', <CustomerDetailPage />)} />
                  <Route path='customer-detail/:id/tab/:tab' element={gate('customer', 'customer', <CustomerDetailPage />)} />
                  <Route path='customer-detail/:id/tab/:tab?/folder/:documentId?' element={gate('customer', 'customer', <CustomerDetailPage />)} />

                  {/* agency */}
                  <Route path='agency' element={<AgencyPage />} />
                  <Route path='agency-detail/:id/:edit?' element={gate('agency', 'supplier', <AgencyDetailPage />)} />
                  <Route path='agency-detail/:id/tab/:tab' element={gate('agency', 'supplier', <AgencyDetailPage />)} />
                  <Route path='agency-detail/:id/tab/:tab?/folder/:documentId?' element={gate('agency', 'supplier', <AgencyDetailPage />)} />

                  {/* Task */}
                  <Route path='task' element={<TaskPage />} />
                  <Route path='timeline' element={<TimelinePage />} />
                  <Route path='attandancegap' element={<AttandanceGapPage />} />
                  <Route path='tasklibrary' element={<TaskLibrary/>} />

                  {/* incident reporting */}
                  <Route path='incidentReporting' element={<IncidentReportingPage />} />
                  <Route path='incidentReporting/:incidentId' element={<IncidentReportingDetailPage/>} />

                  {/* complaint register */}
                  <Route path='complaint' element={<ComplaintRegisterPage />} />
                  <Route path='complaintDetail/:complaintId' element={<ComplainRegisterDetailPage/>} />

                  {/* customer duty requests */}
                  <Route path='servicerequest' element={<CustomerRequestsPage />} />


                  {/* Mobile service Task */}
                  <Route path='mobileServices' element={<MobileResponseServices />} />
                  <Route path='timesheet' element={<TimeSheet />} />

                  {/* key register */}
                  <Route path='keyRegister' element={<KeyRegisterPage/>} />
                  <Route path='keyDetail/:keyId' element={<KeyDetailPage/>} />


                  {/* compliance documents */}
                  <Route path='compliance' element={<CompliancePage />} />
                  {/* appraisals */}
                  <Route path='appraisals' element={<AppraisalsPage />} />
                  {/* surveys */}
                  <Route path='surveys' element={<SurveysPage />} />

                  {/* notifications */}
                  <Route path='notifications' element={<NotificationPage />} />

                  {/* A typo'd/stale in-app link under /user/ (the common case —
                      most navigation happens here) would otherwise render
                      DefaultLayout's chrome with a blank Outlet. Scoped as a real
                      nested child route here, not a sibling <Routes> tree, so no
                      collision risk with the top-level CatchAllRoute above. */}
                  <Route path='*' element={<PageNotFound />} />

                </Route>
              </Routes>
            </Suspense>

            
            {activeSession?._id && (
              <RemoteCursor sessionId={activeSession._id} />
            )}

            {authenticatedUserDetail?._id && !isSupportSession && (
              <WhatsNewModal />
            )}
          </ApolloProvider>


      </BrowserRouter>
    </>
  );
}

export default App;